import { AppDispatch } from '@/store/store';
import { EventType } from '@/models/Interaction';
import { getChatAPI } from '@/api/ChatApi';
import { openReaderWidget } from '@/store/actionCreators/WidgetActions';
import { OpenWidgetPayload } from '@/store/reducers/UIState';
import { startNewThreadFromMessage } from '@/actions/QueryActionHelpers';
import { ThreadId } from '@/models/Thread';
import { WidgetEvent, WidgetType } from '@/models/PanelWidgets';

import { BoundingBox } from '@allenai/pdf-components/src/components/types/boundingBox'; // eslint-disable-line import/no-unresolved
import { PayloadAction } from '@reduxjs/toolkit';

export interface ReaderWidgetTriggerContext {
  corpusId: number;
  boundingBoxes?: string;
}

export function buildReaderActionCreator(
  threadId: ThreadId,
  context: ReaderWidgetTriggerContext,
): PayloadAction<OpenWidgetPayload> | null {
  const { corpusId, boundingBoxes } = context;
  if (typeof corpusId !== 'number') {
    console.error('corpusId is required');
    return null;
  }

  const boundingBoxesList = buildBoundingBoxes(boundingBoxes);
  return openReaderWidget({
    currentThreadId: threadId,
    corpusId,
    highlights: boundingBoxesList,
  });
}

export function buildBoundingBoxes(boundingBoxesStr?: string): BoundingBox[] {
  if (!boundingBoxesStr) {
    return [];
  }

  // when there are multiple bounding boxes, they are separated by a semicolon
  const boxes = boundingBoxesStr.split(';');
  const highlights: BoundingBox[] = [];

  return boxes.reduce((highlightsList, box) => {
    const fields = box.split(',');

    // there should be 5 numbers to build a bounding box
    if (fields.length !== 5) {
      return highlightsList;
    }

    const [top, left, height, width, page] = fields.map(Number);
    highlightsList.push({ left, top, width, height, page } as BoundingBox);
    return highlightsList;
  }, highlights);
}

export async function dispatchReaderAction({
  messageId,
  triggerContextObj,
  dispatch,
}: {
  messageId?: string;
  triggerContextObj: ReaderWidgetTriggerContext;
  dispatch: AppDispatch;
}): Promise<void> {
  if (!messageId) {
    console.log('Missing message id in reader link');
    return;
  }
  const thread = await startNewThreadFromMessage(messageId, dispatch);
  if (!thread) {
    return;
  }

  const readerActionCreator = buildReaderActionCreator(
    thread.id,
    triggerContextObj,
  );

  const chatApi = getChatAPI();
  chatApi.trackEvent({
    type: EventType.UI_INTERACTION,
    threadId: thread.id,
    messageId: thread.messages[0].id,
    data: {
      widget_type: WidgetType.READER,
      event_type: WidgetEvent.WIDGET_OPEN,
      // TODO (#308): pipe through event id here once its available
    },
  });

  if (readerActionCreator) {
    dispatch(readerActionCreator);
  }
}
